import { useRef, useCallback, createContext, useContext } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useNotesShortcuts } from '../hooks/useNotesShortcuts';
import { useNotebooks, useNotesList, getDefaultNotebookId } from '../hooks/useNotes';
import { useNoteEvents } from '../hooks/useNoteEvents';
import NotesSidebar, { type NotesSidebarHandle } from './NotesSidebar';

interface NotesLayoutContextValue {
  openSidebar: () => void;
  focusSearch: () => void;
}

const NotesLayoutContext = createContext<NotesLayoutContextValue>({
  openSidebar: () => {},
  focusSearch: () => {},
});

export function useNotesLayout() {
  return useContext(NotesLayoutContext);
}

export default function NotesLayout() {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const sidebarRef = useRef<NotesSidebarHandle>(null);
  const navigate = useNavigate();
  const { notebooks } = useNotebooks();
  const { createNote } = useNotesList();

  // SSE connection for real-time note updates from filesystem/other tabs
  useNoteEvents();

  const openSidebar = useCallback(() => {
    if (!isDesktop) navigate('/reviewer/notes');
  }, [isDesktop, navigate]);

  const focusSearch = useCallback(() => {
    if (!isDesktop) {
      navigate('/reviewer/notes');
    } else {
      sidebarRef.current?.focusSearch();
    }
  }, [isDesktop, navigate]);

  const handleNewNote = useCallback(async () => {
    const defaultId = getDefaultNotebookId(notebooks);
    if (!defaultId) {
      message.info('Create a notebook first');
      return;
    }
    try {
      const note = await createNote.mutateAsync({
        title: 'Untitled',
        content: '',
        notebookId: defaultId,
      });
      navigate(`/reviewer/notes/${note.id}`);
    } catch {
      message.error('Failed to create note');
    }
  }, [notebooks, createNote, navigate]);

  useNotesShortcuts({
    onNewNote: handleNewNote,
    onFocusSearch: focusSearch,
  });

  const contextValue: NotesLayoutContextValue = { openSidebar, focusSearch };

  if (isDesktop) {
    return (
      <NotesLayoutContext.Provider value={contextValue}>
        <div className="notes-layout">
          <div className="notes-layout-sidebar">
            <NotesSidebar ref={sidebarRef} />
          </div>
          <div className="notes-layout-content">
            <Outlet />
          </div>
        </div>

        <style>{`
          .notes-layout {
            display: flex;
            margin: -24px;
            min-height: calc(100vh - 64px);
          }
          .notes-layout-sidebar {
            width: 280px;
            flex-shrink: 0;
            border-right: 1px solid var(--border-subtle);
            height: calc(100vh - 64px);
            position: sticky;
            top: 64px;
          }
          .notes-layout-content {
            flex: 1;
            min-width: 0;
            padding: 24px;
          }
        `}</style>
      </NotesLayoutContext.Provider>
    );
  }

  // Mobile layout — no drawer, NotesPage acts as the sidebar
  return (
    <NotesLayoutContext.Provider value={contextValue}>
      <Outlet />
    </NotesLayoutContext.Provider>
  );
}
